import {
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { Box, IconButton, Stack, useTheme } from "@mui/material";
import {
  flexRender,
  type Header,
  type HeaderGroup,
  type Table as TableType,
} from "@tanstack/react-table";
import { CheckBox } from "components/checkbox";
import {
  DragHandleIcon,
  SortingDownArrowIcon,
  SortingUpArrowIcon,
  VerticalArrowsIcon,
} from "components/icons";
import type React from "react";

interface TableHeaderProps<TData> {
  table: TableType<TData>;
  enableColumnResizing?: boolean;
  enableColumnReordering?: boolean;
}

export const TableHeader = <TData,>({
  table,
  enableColumnResizing = false,
  enableColumnReordering = false,
}: TableHeaderProps<TData>) => {
  return (
    <Box
      component={"thead"}
      sx={{
        display: "table-header-group",
        width: "100%",
        position: "sticky",
        top: 0,
        bgcolor: "#FFF",
        zIndex: 2,
        boxShadow: "0 1px 0 0px #EDEDED",
      }}
    >
      {table?.getHeaderGroups().map((headerGroup: HeaderGroup<TData>) => (
        <Box
          component={"tr"}
          key={headerGroup.id}
          sx={{ display: "table-row", width: "100%" }}
        >
          <SortableContext
            items={table.getAllLeafColumns().map((c) => c.id)}
            strategy={horizontalListSortingStrategy}
          >
            {headerGroup.headers.map((header: Header<TData, unknown>) => {
              const isCheckbox = header.column.columnDef.header === "select";
              const canReorder = enableColumnReordering && !isCheckbox;

              if (canReorder) {
                return (
                  <DraggableHeaderCell
                    key={header.id}
                    header={header}
                    table={table}
                    enableColumnResizing={enableColumnResizing}
                  />
                );
              }

              return (
                <StaticHeaderCell
                  key={header.id}
                  header={header}
                  table={table}
                  enableColumnResizing={enableColumnResizing}
                />
              );
            })}
          </SortableContext>
        </Box>
      ))}
    </Box>
  );
};

// ── Static header cell (non-draggable: checkbox column or reordering disabled) ──

interface HeaderCellProps<TData> {
  header: Header<TData, unknown>;
  table: TableType<TData>;
  enableColumnResizing: boolean;
}

const StaticHeaderCell = <TData,>({
  header,
  table,
  enableColumnResizing,
}: HeaderCellProps<TData>) => {
  const theme = useTheme();
  const isSortable = header.column.columnDef.meta?.isSortable;
  const isCheckbox = header.column.columnDef.header === "select";
  const isSorted = header.column.getIsSorted();
  const headerSx = header.column.columnDef.meta?.headerSx;
  const canResize = enableColumnResizing && header.column.getCanResize();
  const isResizing = header.column.getIsResizing();
  const hasSized =
    canResize &&
    (isResizing || header.column.id in table.getState().columnSizing);
  const size = hasSized ? header.getSize() : undefined;

  return (
    <Box
      sx={{
        ...(hasSized
          ? {
              width: `${size}px`,
              minWidth: `${size}px`,
              maxWidth: `${size}px`,
              position: "relative",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }
          : {
              minWidth: isCheckbox ? "30px" : "140px",
              maxWidth: isCheckbox ? "70px" : "200px",
              width: isCheckbox ? "70px" : "auto",
              position: canResize ? "relative" : undefined,
            }),
        p: "12px 16px",
        textAlign: "left",
        textWrap: "nowrap",
        ...theme.typography.caption,
        "& .sort-button": {
          visibility: isSorted ? "visible" : "hidden",
        },
        "&:hover": {
          "& .sort-button": {
            visibility: "visible",
          },
          "& .col-resize-handle": {
            opacity: 1,
          },
        },
        ...headerSx,
      }}
      component={"th"}
    >
      <HeaderContent
        header={header}
        table={table}
        isSortable={isSortable}
        isCheckbox={isCheckbox}
        isSorted={isSorted}
      />
      {canResize && <ResizeHandle header={header} isResizing={isResizing} />}
    </Box>
  );
};

// ── Draggable header cell (@dnd-kit/sortable) ──

const DraggableHeaderCell = <TData,>({
  header,
  table,
  enableColumnResizing,
}: HeaderCellProps<TData>) => {
  const theme = useTheme();

  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id: header.column.id });

  const isSortable = header.column.columnDef.meta?.isSortable;
  const isSorted = header.column.getIsSorted();
  const headerSx = header.column.columnDef.meta?.headerSx;
  const canResize = enableColumnResizing && header.column.getCanResize();
  const isResizing = header.column.getIsResizing();
  const hasSized =
    canResize &&
    (isResizing || header.column.id in table.getState().columnSizing);
  const size = hasSized ? header.getSize() : undefined;

  return (
    <Box
      ref={setNodeRef}
      sx={{
        ...(hasSized
          ? {
              width: `${size}px`,
              minWidth: `${size}px`,
              maxWidth: `${size}px`,
              position: "relative",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }
          : {
              minWidth: "140px",
              maxWidth: "200px",
              position: "relative",
            }),
        p: "12px 16px",
        textAlign: "left",
        textWrap: "nowrap",
        ...theme.typography.caption,
        opacity: isDragging ? 0.5 : 1,
        transform: transform
          ? `translate3d(${Math.round(transform.x)}px, 0, 0)`
          : undefined,
        transition: transition ?? undefined,
        zIndex: isDragging ? 3 : undefined,
        "& .sort-button": {
          visibility: isSorted ? "visible" : "hidden",
        },
        "&:hover": {
          "& .sort-button": {
            visibility: "visible",
          },
          "& .col-resize-handle": {
            opacity: 1,
          },
          "& .col-drag-handle": {
            opacity: 1,
          },
        },
        ...headerSx,
      }}
      component={"th"}
    >
      <Box
        ref={setActivatorNodeRef}
        {...listeners}
        {...attributes}
        className="col-drag-handle"
        aria-label="Drag to reorder column"
        sx={{
          position: "absolute",
          left: -4,
          top: 0,
          height: "100%",
          display: "flex",
          alignItems: "center",
          pl: "0px",
          pr: "0px",
          opacity: 0,
          transition: "opacity 120ms ease",
          cursor: isDragging ? "grabbing" : "grab",
          zIndex: 1,
        }}
      >
        <DragHandleIcon
          sx={{
            fontSize: "20px",
            color: theme.palette.lightBg.high,
          }}
        />
      </Box>
      <HeaderContent
        header={header}
        table={table}
        isSortable={isSortable}
        isCheckbox={false}
        isSorted={isSorted}
      />
      {canResize && <ResizeHandle header={header} isResizing={isResizing} />}
    </Box>
  );
};

// ── Shared header content (text + sort buttons + checkbox) ──

interface HeaderContentProps<TData> {
  header: Header<TData, unknown>;
  table: TableType<TData>;
  isSortable?: boolean;
  isCheckbox: boolean;
  isSorted: false | "asc" | "desc";
}

const HeaderContent = <TData,>({
  header,
  table,
  isSortable,
  isCheckbox,
  isSorted,
}: HeaderContentProps<TData>) => {
  return (
    <Stack direction="row" alignItems="center">
      {isCheckbox &&
        (() => {
          const selectableRows = table
            .getRowModel()
            .rows.filter((row) => row.getCanSelect());
          const selectedSelectableRows = selectableRows.filter((row) =>
            row.getIsSelected(),
          );
          const allSelectableSelected =
            selectableRows.length > 0 &&
            selectedSelectableRows.length === selectableRows.length;
          const someSelectableSelected =
            selectedSelectableRows.length > 0 &&
            selectedSelectableRows.length < selectableRows.length;

          return (
            <CheckBox
              checked={allSelectableSelected}
              indeterminate={someSelectableSelected}
              onChange={table.getToggleAllRowsSelectedHandler()}
            />
          );
        })()}
      {header.isPlaceholder || isCheckbox
        ? null
        : flexRender(header.column.columnDef.header, header.getContext())}
      {isSortable && <SortButton header={header} isSorted={isSorted} />}
    </Stack>
  );
};

// ── Sort button ──

interface SortButtonProps<TData> {
  header: Header<TData, unknown>;
  isSorted: false | "asc" | "desc";
}

const SortButton = <TData,>({ header, isSorted }: SortButtonProps<TData>) => {
  if (isSorted) {
    return (
      <Stack
        alignItems={"center"}
        justifyContent={"center"}
        onClick={header.column.getToggleSortingHandler()}
        sx={{ cursor: "pointer", ml: "8px", mt: "6px" }}
      >
        {isSorted === "asc" ? (
          <SortingUpArrowIcon style={{ width: "16px", height: "16px" }} />
        ) : (
          <SortingDownArrowIcon style={{ width: "16px", height: "16px" }} />
        )}
      </Stack>
    );
  }

  return (
    <IconButton
      className="sort-button"
      onClick={header.column.getToggleSortingHandler()}
      aria-label="Sort column"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        p: 0,
        ":hover": {
          backgroundColor: "transparent",
        },
      }}
    >
      <VerticalArrowsIcon />
    </IconButton>
  );
};

// ── Resize handle ──

interface ResizeHandleProps<TData> {
  header: Header<TData, unknown>;
  isResizing: boolean;
}

const ResizeHandle = <TData,>({
  header,
  isResizing,
}: ResizeHandleProps<TData>) => {
  const theme = useTheme();

  return (
    <Box
      className="col-resize-handle"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize column"
      onMouseDown={(e: React.MouseEvent) => {
        e.stopPropagation();
        header.getResizeHandler()(e);
      }}
      onTouchStart={(e: React.TouchEvent) => {
        e.stopPropagation();
        header.getResizeHandler()(e);
      }}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      sx={{
        position: "absolute",
        top: 0,
        right: 0,
        height: "100%",
        width: "6px",
        cursor: "col-resize",
        userSelect: "none",
        touchAction: "none",
        opacity: isResizing ? 1 : 0,
        transition: "opacity 120ms ease",
        zIndex: 3,
        "&::after": {
          content: '""',
          position: "absolute",
          top: "20%",
          bottom: "20%",
          right: "2px",
          width: "2px",
          borderRadius: "1px",
          backgroundColor: isResizing
            ? theme.palette.primary.main
            : theme.palette.neutral["015"],
        },
        "&:hover::after": {
          backgroundColor: theme.palette.primary.main,
        },
      }}
    />
  );
};
